import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Card } from "reactstrap";

import {
  BlockTitle,
  Row,
  Col,
  Button,
  OutlinedInput,
  InputSwitch,
  RSelect,
} from "../../components/Component";
import DropZoneInput from "../../components/input/dropzone/DropzoneInput";
import FormWithLabel from "../../components/input/label/FormWithLabel";
import RichTextEditor from "../../components/input/rich-text/RichTextEditor";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";

import partnerService from "../../services/partnerService";
import { useBeManualFetcher } from "../../hooks/useBeManualFetcher";
import {
  LANGUAGE_KEYS,
  PARTNER_CATEGORIES,
  PARTNER_CATEGORIES_READABLE,
} from "../../utilities/constants";
import { createEmptyRichText } from "../../utilities/formatUtils";

const PartnerCreatePage = () => {
  const navigate = useNavigate();
  const { errors, register, handleSubmit } = useForm();
  const [onFetch, isLoading] = useBeManualFetcher();

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryError, setCategoryError] = useState(false);
  const [showInHomeSlider, setShowInHomeSlider] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [logo, setLogo] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [uzDescription, setUzDescription] = useState(createEmptyRichText);
  const [ruDescription, setRuDescription] = useState(createEmptyRichText);
  const [enDescription, setEnDescription] = useState(createEmptyRichText);

  const categoryOptions = useMemo(
    () =>
      Object.values(PARTNER_CATEGORIES).map((category) => ({
        value: category,
        label: PARTNER_CATEGORIES_READABLE[category],
      })),
    []
  );

  const descriptionFields = [
    { label: "O'zbekcha", value: uzDescription, setValue: setUzDescription },
    { label: "На русском", value: ruDescription, setValue: setRuDescription },
    { label: "In English", value: enDescription, setValue: setEnDescription },
  ];

  async function onFormSubmit(form) {
    if (!selectedCategory) {
      setCategoryError(true);
      return;
    }

    const { uzName, ruName, enName, phoneNumber, orderIndex } = form;

    const data = {
      category: selectedCategory.value,
      name: {
        [LANGUAGE_KEYS.UZ]: uzName,
        [LANGUAGE_KEYS.RU]: ruName,
        [LANGUAGE_KEYS.EN]: enName,
      },
      description: {
        [LANGUAGE_KEYS.UZ]: JSON.stringify(uzDescription),
        [LANGUAGE_KEYS.RU]: JSON.stringify(ruDescription),
        [LANGUAGE_KEYS.EN]: JSON.stringify(enDescription),
      },
      orderIndex: orderIndex ? Number(orderIndex) : null,
      showInHomeSlider,
      isActive,
      phoneNumber,
    };

    return await onFetch({
      action: () =>
        partnerService.createPartner({ data, logo: logo?.[0], gallery }),
      onLoad: () => navigate("/partners", { replace: true }),
      successMessage: "Partner created successfully",
    });
  }

  return (
    <React.Fragment>
      <Head title={"Create Partner"}></Head>
      <Content>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <Button
              color="white border-light"
              className="mb-2"
              onClick={() => navigate("/partners")}
              iconName="arrow-left"
              title={"Back"}
            />
            <BlockTitle tag="h4" className="mb-0">
              Create Partner
            </BlockTitle>
          </div>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)}>
          <Card className="card-bordered card-inner mb-4">
            <BlockTitle tag="h6" className="mb-3 text-muted">
              Basic Information
            </BlockTitle>

            <Row className="g-3">
              <Col size="12">
                <FormWithLabel noWrap label={"Name"}>
                  <Row className="g-2">
                    {[
                      { name: "uzName", label: "O'zbekcha" },
                      { name: "ruName", label: "На русском" },
                      { name: "enName", label: "In English" },
                    ].map((field) => (
                      <Col size="4" key={field.name}>
                        <OutlinedInput
                          type="text"
                          label={field.label}
                          name={field.name}
                          forwardRef={register({
                            required: "Nom kiritilishi shart",
                          })}
                          errors={errors}
                        />
                      </Col>
                    ))}
                  </Row>
                </FormWithLabel>
              </Col>

              <Col size="4">
                <FormWithLabel label={"Category *"} noWrap>
                  <RSelect
                    options={categoryOptions}
                    onChange={(option) => {
                      setSelectedCategory(option);
                      setCategoryError(false);
                    }}
                    value={selectedCategory}
                    placeholder="Select category"
                  />
                  {categoryError && (
                    <span className="invalid">Kategoriya tanlanishi shart</span>
                  )}
                </FormWithLabel>
              </Col>
              <Col size="4">
                <FormWithLabel label={"Contact"} noWrap>
                  <OutlinedInput
                    type="text"
                    label="Phone number"
                    name="phoneNumber"
                    forwardRef={register({})}
                    errors={errors}
                  />
                </FormWithLabel>
              </Col>
              <Col size="4">
                <FormWithLabel
                  label={"Display order"}
                  inputHint="Kichik raqam oldinroq"
                  noWrap
                >
                  <OutlinedInput
                    type="number"
                    label="Order index"
                    name="orderIndex"
                    forwardRef={register({})}
                    errors={errors}
                  />
                </FormWithLabel>
              </Col>

              <Col size="12">
                <div className="d-flex flex-wrap align-items-center pt-2">
                  <div className="mr-5">
                    <InputSwitch
                      id="showInHomeSlider"
                      label="Show in home slider"
                      onClick={() => setShowInHomeSlider((prev) => !prev)}
                      checked={showInHomeSlider}
                    />
                  </div>
                  <div>
                    <InputSwitch
                      id="isActive"
                      label="Active"
                      onClick={() => setIsActive((prev) => !prev)}
                      checked={isActive}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          <Card className="card-bordered card-inner mb-4">
            <BlockTitle tag="h6" className="mb-3 text-muted">
              Description
            </BlockTitle>

            <Row className="g-3">
              {descriptionFields.map((field) => (
                <Col size="12" key={field.label}>
                  <FormWithLabel label={field.label} noWrap>
                    <RichTextEditor
                      value={field.value}
                      setValue={field.setValue}
                    />
                  </FormWithLabel>
                </Col>
              ))}
            </Row>
          </Card>

          <Card className="card-bordered card-inner mb-4">
            <BlockTitle tag="h6" className="mb-3 text-muted">
              Logo & Gallery
            </BlockTitle>

            <Row className="g-3">
              <Col size="4">
                <FormWithLabel label={"Logo"} inputHint="Bitta rasm" noWrap>
                  <DropZoneInput
                    text={"Drag and drop the logo, or click to select"}
                    files={logo}
                    setFiles={setLogo}
                  />
                </FormWithLabel>
              </Col>
              <Col size="8">
                <FormWithLabel
                  label={"Gallery"}
                  inputHint="Bir nechta rasm"
                  noWrap
                >
                  <DropZoneInput
                    text={"Drag and drop the images, or click to select"}
                    files={gallery}
                    setFiles={setGallery}
                    multiple
                  />
                </FormWithLabel>
              </Col>
            </Row>
          </Card>

          <div className="d-flex justify-content-end pb-4">
            <Button
              color="white border-light"
              className="mr-2"
              onClick={(e) => {
                e.preventDefault();
                navigate("/partners");
              }}
              title={"Cancel"}
            />
            <Button
              color="primary"
              type="submit"
              iconName="save"
              title={"Save"}
              isLoading={isLoading}
            />
          </div>
        </form>
      </Content>
    </React.Fragment>
  );
};

export default PartnerCreatePage;
